import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { DashboardLayout } from "../dashboard/DashboardLayout";
import { addGiftToSchemeAction } from "../../redux/actions/GiftAction";
import { toast } from "react-toastify";
import { FaArrowLeft } from "react-icons/fa";
import { FiUploadCloud } from "react-icons/fi";
import { FullScreenLoader } from "../Components/Loader";
import axiosInstance from "../../api/axiosInstance";
import { fetchKarnatakaCities } from "../../redux/actions/customerActions";
import CityMultiSelectDropdown from "../Components/CityMultiSelectDropdown";
const AddGiftToScheme = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { userInfo } = useSelector((state) => state.auth);
  const { loading } = useSelector((state) => state.gift || {});
  const { karnatakaCities = [], citiesLoading } = useSelector(
    (state) => state.fetchCustomerList || {},
  );
  const fileInputRef = useRef(null);
  const BASE_URL = axiosInstance.defaults.baseURL;
  const orderType = (
    userInfo?.user_order_type ||
    userInfo?.data?.user_order_type ||
    userInfo?.order_type ||
    userInfo?.user_type ||
    "loyality"
  ).toLowerCase();

  const isPromotional = orderType === "promotional";
  const qrLabel = isPromotional ? "Points" : "QR Count";
  const scheme = location.state?.scheme || {};

  const [formData, setFormData] = useState({
    gift_name: "",
    gift_image: "",
    preview: null,
    qr_count: 0,
    is_additional_gift: false,
    is_live: true,
    cities: [], // ← added
  });

  useEffect(() => {
    dispatch(fetchKarnatakaCities(userInfo.access_token));
  }, []);

  const formatExpiredAt = (value) => {
    if (!value) return null;
    const [datePart, timePart] = value.split("T");
    if (!datePart || !timePart) return null;
    const seconds = timePart.length === 5 ? `${timePart}:00` : timePart;
    return `${datePart} ${seconds}`;
  };
  // Returns current local time as "YYYY-MM-DDTHH:mm" for use as the `min` on datetime-local inputs
  const getCurrentDateTimeLocal = () => {
    const now = new Date();
    const offset = now.getTimezoneOffset();
    const localDate = new Date(now.getTime() - offset * 60 * 1000);
    return localDate.toISOString().slice(0, 16);
  };

  const convertImageToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const base64String = reader.result;
        resolve(base64String);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (file) {
      try {
        const base64Image = await convertImageToBase64(file);
        setFormData((prev) => ({
          ...prev,
          gift_image: base64Image,
          preview: base64Image,
        }));
      } catch (error) {
        toast.error("Failed to process image");
      }
    }
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };
  const handleExpiredAtChange = (value) => {
    if (value) {
      const selected = new Date(value);
      const now = new Date();
      now.setSeconds(0, 0); // truncate to minute precision, matching datetime-local's granularity
      if (selected < now) {
        toast.error("Expiry date cannot be in the past");
        return;
      }
    }
    handleInputChange("expired_at", value);
  };
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.gift_name.trim()) {
      toast.error("Gift name is required");
      return;
    }

    if (!formData.gift_image) {
      toast.error("Gift image is required");
      return;
    }
    if (!formData.qr_count || Number(formData.qr_count) <= 0) {
      toast.error(`Please enter a valid ${qrLabel} (greater than 0)`);
      return;
    }

    try {
      // Format expired_at if provided
      let expiredAtFormatted = null;
      if (formData.expired_at) {
        // Convert datetime-local format to YYYY-MM-DD HH:MM:SS
        const date = new Date(formData.expired_at);
        expiredAtFormatted = date.toISOString().slice(0, 19).replace("T", " ");
      }

      const payload = {
        offer_scheme_name: scheme.scheme_name,
        gift_name: formData.gift_name,
        gift_image: formData.gift_image,
        qr_count: formData.qr_count || 0,
        is_additional_gift: formData.is_additional_gift || false,
        is_live: formData.is_live !== undefined ? formData.is_live : true,
        expired_at: expiredAtFormatted,
      };
      if (formData.cities && formData.cities.length > 0) {
        payload.cities = formData.cities;
      }
      const result = await dispatch(
        addGiftToSchemeAction(payload, userInfo.access_token),
      );

      if (result.type === "ADD_GIFT_TO_SCHEME_SUCCESS") {
        toast.success("Gift added to scheme successfully!");
        navigate("/dashboard/giftList");
      } else {
        toast.error(result.payload?.message || "Failed to add gift to scheme");
      }
    } catch (error) {
      console.error("Submit error:", error);
      toast.error("Failed to add gift to scheme. Please try again.");
    }
  };

  return (
    <DashboardLayout>
      <FullScreenLoader
        show={loading}
        message={loading ? "Adding gift to scheme..." : ""}
        variant="primary"
        blurIntensity="md"
      />
      <div className="mt-2 max-w-full mx-auto rounded-lg p-2">
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center mb-6">
            <button
              onClick={() => navigate(-1)}
              className="mr-3 hover:text-gray-800"
            >
              <FaArrowLeft size={20} />
            </button>
            <h2 className="text-2xl font-bold text-amber-600">
              Add Gift to Scheme
            </h2>
          </div>

          <div className="mb-4 p-3 bg-blue-50 rounded-lg">
            <p className="text-sm text-gray-700">
              <span className="font-semibold">Scheme:</span>{" "}
              {scheme.scheme_name || "N/A"}
            </p>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="space-y-6">
              {/* Gift Name */}
              <div className="relative">
                <input
                  type="text"
                  value={formData.gift_name}
                  onChange={(e) =>
                    handleInputChange("gift_name", e.target.value)
                  }
                  className="peer w-full border border-gray-300 rounded-md px-3 pt-5 pb-2 text-sm focus:outline-none focus:border-amber-500"
                  placeholder=" "
                  required
                />
                <label className="pointer-events-none absolute left-2.5 -top-2 text-xs bg-white px-1 transition-all duration-200 text-gray-500 peer-placeholder-shown:top-3.5 peer-placeholder-shown:text-sm peer-placeholder-shown:text-gray-400 peer-focus:-top-2 peer-focus:text-xs peer-focus:text-amber-500">
                  Gift Name <span className="text-red-500">*</span>
                </label>
              </div>

              {/* Image Upload */}
              <div>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleImageUpload}
                  accept="image/png,image/jpeg,image/webp"
                  className="hidden"
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="w-full border-2 border-dashed border-amber-300 rounded-lg px-3 py-3 hover:border-amber-500 hover:bg-amber-50 transition duration-300 flex items-center justify-center gap-2 text-sm text-gray-600 hover:text-amber-600"
                >
                  <FiUploadCloud size={18} />
                  {formData.gift_image ? "Change Image" : "Upload Gift Image"}
                </button>
                {formData.preview && (
                  <div className="mt-2 flex justify-center">
                    <img
                      src={formData.preview}
                      alt="Preview"
                      className="h-32 w-32 object-cover rounded-md border border-gray-300"
                      onError={(e) => {
                        e.target.src =
                          "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='128' height='128'%3E%3Crect width='128' height='128' fill='%23ddd'/%3E%3Ctext x='50%' y='50%' text-anchor='middle' dy='.3em' fill='%23999' font-size='12'%3ENo Image%3C/text%3E%3C/svg%3E";
                      }}
                    />
                  </div>
                )}
              </div>

              {/* QR Count */}
              <div className="relative">
                <input
                  type="number"
                  value={formData.qr_count}
                  onChange={(e) => {
                    const val = e.target.value;
                    const parsed = parseInt(val, 10);
                    if (parsed === 0) return; // reject zero
                    handleInputChange(
                      "qr_count",
                      val === "" ? "" : parsed || "",
                    );
                  }}
                  min="0"
                  onWheel={(e) => e.target.blur()}
                  className="peer w-full border border-gray-300 rounded-md px-3 pt-5 pb-2 text-sm focus:outline-none focus:border-amber-500"
                  placeholder=" "
                />
                <label className="pointer-events-none absolute left-2.5 -top-2 text-xs bg-white px-1 transition-all duration-200 text-gray-500 peer-placeholder-shown:top-3.5 peer-placeholder-shown:text-sm peer-placeholder-shown:text-gray-400 peer-focus:-top-2 peer-focus:text-xs peer-focus:text-amber-500">
                  {/* QR Count */} {qrLabel}
                </label>
              </div>

              {/* Cities (optional) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Cities
                  <span className="text-xs text-gray-400 font-normal ml-2">
                    (optional — leave empty to apply to all cities)
                  </span>
                </label>
                <CityMultiSelectDropdown
                  cities={karnatakaCities}
                  selected={formData.cities || []}
                  loading={citiesLoading}
                  onChange={(newCities) =>
                    handleInputChange("cities", newCities)
                  }
                />
              </div>
              {/* Additional Gift Checkbox */}
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="is_additional_gift"
                  checked={formData.is_additional_gift}
                  onChange={(e) =>
                    handleInputChange("is_additional_gift", e.target.checked)
                  }
                  className="w-4 h-4 text-amber-500 border-gray-300 rounded focus:ring-amber-500"
                />
                <label
                  htmlFor="is_additional_gift"
                  className="ml-2 text-sm text-gray-700"
                >
                  Additional Gift (Bumper Prize)
                </label>
              </div>

              {/* Is Live Checkbox */}
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="is_live"
                  checked={formData.is_live}
                  onChange={(e) =>
                    handleInputChange("is_live", e.target.checked)
                  }
                  className="w-4 h-4 text-amber-500 border-gray-300 rounded focus:ring-amber-500"
                />
                <label htmlFor="is_live" className="ml-2 text-sm text-gray-700">
                  Is Live
                </label>
              </div>

              {/* Expired At */}
              <div className="relative">
                <input
                  type="datetime-local"
                  value={formData.expired_at}
                  onChange={(e) => handleExpiredAtChange(e.target.value)}
                  min={getCurrentDateTimeLocal()}
                  className="peer w-full border border-gray-300 rounded-md px-3 pt-5 pb-2 text-sm focus:outline-none focus:border-amber-500"
                />
                <label className="pointer-events-none absolute left-2.5 -top-2 text-xs bg-white px-1 transition-all duration-200 text-gray-500 peer-placeholder-shown:top-3.5 peer-placeholder-shown:text-sm peer-placeholder-shown:text-gray-400 peer-focus:-top-2 peer-focus:text-xs peer-focus:text-amber-500">
                  Expired At (Optional - Leave empty for Lifetime)
                </label>
              </div>
            </div>

            {/* Submit and Cancel Buttons */}
            <div className="flex gap-3 mt-6">
              <button
                type="submit"
                disabled={loading}
                className={`w-auto bg-amber-500 text-white py-2 px-6 rounded-lg hover:bg-amber-600 transition duration-300 font-medium ${
                  loading ? "opacity-50 cursor-not-allowed" : ""
                }`}
              >
                {loading ? "Adding..." : "Add Gift to Scheme"}
              </button>
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="px-6 py-2 bg-gray-300 hover:bg-gray-400 text-gray-800 rounded-lg transition duration-300 font-medium"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default AddGiftToScheme;
